/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { TooltipLayer } from '@/components/TooltipLayer';
import { GlobalProgress } from '@/components/loading/GlobalProgress';
import { PageLoading } from '@/components/loading/PageLoading';
import { useDismissBootSplash } from '@/components/loading/bootSplash';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/uiStore';

import { TourInvite } from '@/features/tour/TourInvite';
import { TourOverlay } from '@/features/tour/TourOverlay';
import { useMediaQuery } from '@/design-system';
import { Rail } from './shell/Rail';
import { RAIL_DOCK_QUERY, useRail } from './shell/useRail';
import { Topbar } from './shell/Topbar';
import { RoleViewBanner } from './shell/RoleViewBanner';
import { DisclaimerModal } from './shell/DisclaimerModal';
import { FeedbackFab } from './shell/FeedbackFab';
import { ResourceLinksModal } from './shell/ResourceLinksModal';
import { REMEMBERED_ROUTES, rememberedBase, vehicleQuery, vehicleQueryOfUrl } from './shell/nav';
import { useMetricsTracking } from './shell/useMetricsTracking';
import { useSyncUserSettings } from './shell/useSyncUserSettings';
import { useEnsureUserSession } from './shell/useEnsureUserSession';
import './shell/shell.css';
import './shell/modals.css';

/** Width range in which the rail is an off-canvas drawer; mirrors the phone block in shell.css. */
const DRAWER_QUERY = '(max-width: 599.98px)';

/**
 * Renders nothing; exists to be a sibling of the routed outlet. Suspense commits its children only
 * once none of them are suspended, so this dates the splash dismissal to the route having painted
 * rather than the shell having mounted.
 */
function RoutePainted(): null {
  useDismissBootSplash();
  return null;
}

/** EWD application chrome: icon rail + topbar + routed content. */
export function AppShell() {
  const { t } = useTranslation();
  useMetricsTracking();
  useEnsureUserSession();
  useSyncUserSettings();
  const pickerOpen = useUiStore((s) => s.pickerOpen);
  const navOpen = useUiStore((s) => s.navOpen);
  const closeNav = useUiStore((s) => s.closeNav);
  const resourcesOpen = useUiStore((s) => s.resourcesOpen);
  const closeResources = useUiStore((s) => s.closeResources);
  const rememberRoute = useUiStore((s) => s.rememberRoute);
  const tourOpen = useUiStore((s) => s.tourOpen);
  const tourKind = useUiStore((s) => s.tourKind);
  const setRailPeek = useUiStore((s) => s.setRailPeek);
  const rail = useRail();
  const docked = useMediaQuery(RAIL_DOCK_QUERY);
  const { pathname, search } = useLocation();

  // Remember URL for query-driven tools so the rail can restore them, and keep the current
  // vehicle in sync across the two tools: selecting a vehicle in MECL should carry into Schematic
  // Explorer and vice versa. Re-pointing a sibling costs it its own state (tab, selection), so we
  // only do it when the vehicle actually differs -- otherwise switching tools back and forth would
  // reset the tool you came from.
  useEffect(() => {
    const base = rememberedBase(pathname);
    if (!base) return;
    rememberRoute(base, `${pathname}${search}`);
    const vehQuery = vehicleQuery(new URLSearchParams(search));
    if (!vehQuery) return;
    const { routeMemory } = useUiStore.getState();
    for (const other of REMEMBERED_ROUTES) {
      if (other === base) continue;
      const remembered = routeMemory[other];
      if (remembered && vehicleQueryOfUrl(remembered) === vehQuery) continue;
      rememberRoute(other, `${other}?${vehQuery}`);
    }
  }, [pathname, search, rememberRoute]);

  // Close the mobile nav drawer, and a rail opened over the page, after route changes.
  useEffect(() => {
    closeNav();
    setRailPeek(false);
  }, [pathname, closeNav, setRailPeek]);

  // The rail is only a drawer below 600px (shell.css). Growing past that - a rotation, a resized
  // window - closes it, so the page is never left inert behind a drawer that is no longer one.
  // Shrinking into it closes a rail opened over the page, which the drawer has no room for.
  useEffect(() => {
    const drawer = window.matchMedia(DRAWER_QUERY);
    const onChange = () => {
      if (drawer.matches) setRailPeek(false);
      else closeNav();
    };
    drawer.addEventListener('change', onChange);
    return () => drawer.removeEventListener('change', onChange);
  }, [closeNav, setRailPeek]);

  // Likewise at the dock width: a rail opened over a tablet layout is not reopened by coming back.
  useEffect(() => {
    setRailPeek(false);
  }, [docked, setRailPeek]);

  return (
    <div
      className={cn(
        'ewd',
        pickerOpen && 'vp-open',
        navOpen && 'nav-open',
        rail.open && 'rail-open',
        rail.over && 'rail-over',
      )}
    >
      {navOpen && (
        <button
          type="button"
          className="rail-backdrop"
          aria-label={t('common.close', 'Close')}
          onClick={closeNav}
        />
      )}
      <Rail />
      {/* While the drawer is open it is the only thing that takes focus or clicks. */}
      <div className="ewd-main" inert={navOpen}>
        <Topbar />
        <RoleViewBanner />
        <main className="ewd-home">
          <Suspense fallback={<PageLoading />}>
            <RoutePainted />
            <Outlet />
          </Suspense>
        </main>
      </div>
      <GlobalProgress />
      <FeedbackFab />
      <DisclaimerModal />
      {resourcesOpen && <ResourceLinksModal onClose={closeResources} />}
      <TooltipLayer />
      {/* Tours never start on their own: a first visit is offered the page's tour, once. */}
      <TourInvite />
      {tourOpen && <TourOverlay key={tourKind} />}
    </div>
  );
}
